import React, { useEffect, useState, useRef, useCallback } from 'react'
import localDB from '../../../../crud/localDB/localDB'
import { getAllTerminalsByLocalId } from '../../../../crud/management_api/terminals'
import { getWorkstationsByLocalId } from '../../../../crud/management_api/workstations'
import { GetAllNotReadyComandasByLocalId } from '../../../../crud/pedidos_api/comandas'
import { useNavigate } from 'react-router-dom'
import './KDS.scss'
import {
  Comanda,
  NewComandaBatchEvent,
  Workstation,
  Terminal,
  ComandaItem,
  ComandaDb
} from './types'
import ComandaCard from './components/ComandaCard/ComandaCard'
import LoadingSpinner from '../../../../components/common/LoadingSpinner'
import Header from './components/Header/Header'

// entry type for your UI
interface Entry {
  entryId: number         // outer comanda_id
  comanda: Comanda
  batchId: number
  companions: ComandaItem[]
  status: 'start' | 'ready'
  timestamp: number
}

// your 8-color palette
const BATCH_COLORS = [
  '#FC7556','#FFA726','#66BB6A','#42A5F5',
  '#AB47BC','#26A69A','#D4E157','#FF7043'
]

const WS_URL = 'wss://p3lb9s35ke.execute-api.us-east-2.amazonaws.com/production/'

const KDS: React.FC = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [workstations, setWorkstations] = useState<Workstation[]>([])
  const [terminals, setTerminals] = useState<Terminal[]>([])
  const [entries, setEntries] = useState<Entry[]>([])
  const batchColorRef = useRef(new Map<number,string>())
  const nextBatchId = useRef(1)
  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimeoutRef = useRef<number | null>(null)
  const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'disconnected'>('connecting')

  const [scale, setScale] = useState(1)
  const zoomIn  = () => setScale(s => Math.min(2, s + 0.1))
  const zoomOut = () => setScale(s => Math.max(0.5, s - 0.1))

  // Load persisted zoom on mount
  useEffect(() => {
    (async () => {
      try {
        const persisted = await localDB.get<number>('kds', 'zoom')
        if (persisted && typeof persisted === 'number') {
          setScale(Math.min(2, Math.max(0.5, persisted)))
        }
      } catch {
        // ignore
      }
    })()
  }, [])

  // Persist zoom changes
  useEffect(() => {
    (async () => {
      try {
        await localDB.set('kds', 'zoom', Number(scale.toFixed(2)))
      } catch {
        // ignore
      }
    })()
  }, [scale])

  // device UUID
  const [deviceId] = useState(() => {
    const key = 'kdsDeviceId'
    const ex = localStorage.getItem(key)
    if (ex) return ex
    const id = crypto.randomUUID()
    localStorage.setItem(key, id)
    return id
  })

  // localId
  const user = JSON.parse(localStorage.getItem('user') || '{}')
  const localId: number = user?.local_id ?? 0

  // Manual/auto refresh function (guarded to avoid overlapping refreshes)
  const isRefreshingRef = useRef(false)
  const handleRefresh = useCallback(() => {
    if (isRefreshingRef.current) return
    isRefreshingRef.current = true

    // Reconnect WebSocket
    if (wsRef.current) {
      wsRef.current.close()
    }

    // Refresh data
    setLoading(true)
    Promise.all([
      getWorkstationsByLocalId(String(localId)),
      getAllTerminalsByLocalId(String(localId)),
      GetAllNotReadyComandasByLocalId(localId)
    ])
      .then(([wsRes, tRes, cRes]: any[]) => {
        setWorkstations(wsRes.data)
        setTerminals(tRes.data)

        const dbList = cRes.data
        const bId = 0
        batchColorRef.current.set(bId, BATCH_COLORS[0])

        const companionsMap: Record<number,ComandaItem[]> = {}
        dbList.forEach((cdb: ComandaDb) => {
          companionsMap[cdb.comanda_id] = dbList
            .filter((o: ComandaDb) => o.pedido_id === cdb.pedido_id && o.comanda_id !== cdb.comanda_id)
            .flatMap((o: ComandaDb) => (o.comandaItems.items as ComandaItem[]))
        })

        const refreshedEntries: Entry[] = dbList.map((cdb: ComandaDb) => ({
          entryId: cdb.comanda_id,
          comanda: {
            comanda_id: cdb.comanda_id,
            info: {
              mesa: cdb.comandaItems.info.mesa,
              type: cdb.comandaItems.info.type,
              user: cdb.comandaItems.info.user,
              client: cdb.comandaItems.info.client,
              third_party_provider_name: cdb.comandaItems.info.third_party_provider_name,
              third_party_provider_id: cdb.comandaItems.info.third_party_provider_id,
              third_party_provider_code: cdb.comandaItems.info.third_party_provider_code,
              pedido_id: cdb.pedido_id,
              workstation_id: cdb.workstation_id,
              pedido_type_id: cdb.comandaItems.info.pedido_type_id,
              pedido_type_name: cdb.comandaItems.info.pedido_type_name,
              prioridad: cdb.comandaItems.info.prioridad,
              hora_entrega: cdb.comandaItems.info.hora_entrega
            },
            items: cdb.comandaItems.items as ComandaItem[],
            terminal: {
              terminal_id: cdb.comandaItems.terminal.terminal_id,
              terminal_name: cdb.comandaItems.terminal.terminal_name,
              terminal_type_id: cdb.comandaItems.terminal.terminal_type_id,
              ip_address: cdb.comandaItems.terminal.ip_address,
              state: cdb.comandaItems.terminal.state
            }
          },
          batchId: bId,
          companions: companionsMap[cdb.comanda_id] || [],
          status: cdb.started ? 'ready' : 'start',
          timestamp: new Date(cdb.local_timestamp).getTime()
        }))

        setEntries(refreshedEntries)
      })
      .catch(console.error)
      .finally(() => {
        setLoading(false)
        isRefreshingRef.current = false
      })
  }, [localId])

  // Auto-refresh every 6 minutes
  useEffect(() => {
    const SIX_MIN_MS = 6 * 60 * 1000
    const id = window.setInterval(() => {
      handleRefresh()
    }, SIX_MIN_MS)
    return () => clearInterval(id)
  }, [handleRefresh])

  // fetch initial data + persisted comandas
  useEffect(() => {
    Promise.all([
      getWorkstationsByLocalId(String(localId)),
      getAllTerminalsByLocalId(String(localId)),
      GetAllNotReadyComandasByLocalId(localId)
    ])
      .then(([wsRes, tRes, cRes]: any[]) => {
        setWorkstations(wsRes.data)
        setTerminals(tRes.data)

        const dbList = cRes.data // array of objects matching your JSON

        // create a "batch 0" color
        const bId = 0
        batchColorRef.current.set(bId, BATCH_COLORS[0])

        // build companions map by pedido_id
        const companionsMap: Record<number,ComandaItem[]> = {}
        dbList.forEach((cdb: ComandaDb) => {
          companionsMap[cdb.comanda_id] = dbList
            .filter((o: ComandaDb) => o.pedido_id === cdb.pedido_id && o.comanda_id !== cdb.comanda_id)
            .flatMap((o: ComandaDb) =>
              // each comandaItems.items is your array of { text, comment }
              (o.comandaItems.items as ComandaItem[])
            )
        })

        // map DB objects → UI entries
        const initialEntries: Entry[] = dbList.map((cdb: ComandaDb) => ({
          entryId: cdb.comanda_id,
          comanda: {
            comanda_id: cdb.comanda_id,
            info: {
              mesa: cdb.comandaItems.info.mesa,
              type: cdb.comandaItems.info.type,
              user: cdb.comandaItems.info.user,
              client: cdb.comandaItems.info.client,
              third_party_provider_name: cdb.comandaItems.info.third_party_provider_name,
              third_party_provider_id: cdb.comandaItems.info.third_party_provider_id,
              third_party_provider_code: cdb.comandaItems.info.third_party_provider_code,
              pedido_id: cdb.pedido_id,
              workstation_id: cdb.workstation_id,
              pedido_type_id: cdb.comandaItems.info.pedido_type_id,
              pedido_type_name: cdb.comandaItems.info.pedido_type_name,
              prioridad: cdb.comandaItems.info.prioridad,
              hora_entrega: cdb.comandaItems.info.hora_entrega
            },
            items: cdb.comandaItems.items as ComandaItem[],
            terminal: {
              terminal_id: cdb.comandaItems.terminal.terminal_id,
              terminal_name: cdb.comandaItems.terminal.terminal_name,
              terminal_type_id: cdb.comandaItems.terminal.terminal_type_id,
              ip_address: cdb.comandaItems.terminal.ip_address,
              state: cdb.comandaItems.terminal.state
            }
          },
          batchId: bId,
          companions: companionsMap[cdb.comanda_id] || [],
          status: cdb.started ? 'ready' : 'start',
          timestamp: new Date(cdb.local_timestamp).getTime()
        }))

        setEntries(initialEntries)
      })
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [localId])

  // map terminal → workstation
  const terminalMap = new Map<number, Terminal>()
  terminals.forEach(t => terminalMap.set(t.terminal_id, t))

  // WebSocket with aggressive reconnect
  useEffect(() => {
    let isUnmounted = false

    const connect = () => {
      // If the component has unmounted, do not create a new socket
      if (isUnmounted) return

      // Clear any existing ws instance
      if (wsRef.current) {
        wsRef.current.onopen = null
        wsRef.current.onmessage = null
        wsRef.current.onclose = null
        wsRef.current.onerror = null
        wsRef.current.close()
        wsRef.current = null
      }

      const ws = new WebSocket(WS_URL)
      wsRef.current = ws

      ws.onopen = () => {
        setConnectionStatus('connected')
        // As soon as it's open, register this device/local
        ws.send(
          JSON.stringify({
            action: 'handler',
            data: { device_id: deviceId, local_id: localId, type: 'saveSocket' }
          })
        )
      }

      ws.onmessage = (evt) => {
        let msg: any
        try {
          msg = JSON.parse(evt.data)
        } catch {
          return
        }
        if (msg.event === 'new_comanda_batch' && Array.isArray(msg.data?.comandas)) {
          const batch = msg as NewComandaBatchEvent
          const now = Date.now()
          const bId = nextBatchId.current++
          const color = BATCH_COLORS[(bId - 1) % BATCH_COLORS.length]
          batchColorRef.current.set(bId, color)

          const coms = batch.data.comandas
          const companionsMap: Record<number, ComandaItem[]> = {}
          coms.forEach((c) =>
            (companionsMap[c.comanda_id] = coms
              .filter((o) => o.comanda_id !== c.comanda_id)
              .flatMap((o) => o.comanda.items))
          )

          const newEs: Entry[] = coms.map((entry) => ({
            entryId: entry.comanda_id,
            comanda: entry.comanda,
            batchId: bId,
            companions: companionsMap[entry.comanda_id] || [],
            status: 'start',
            timestamp: now
          }))

          setEntries((prev) => [...prev, ...newEs])
        }
      }

      ws.onclose = () => {
        setConnectionStatus('disconnected')
        // If not unmounted, immediately schedule a reconnect
        if (!isUnmounted) {
          setConnectionStatus('connecting')
          reconnectTimeoutRef.current = window.setTimeout(connect, 1000)
        }
      }

      ws.onerror = () => {
        // On error, force‐close to trigger onclose → reconnect
        ws.close()
      }
    }

    // Kick off the first connect
    connect()

    // Cleanup on unmount
    return () => {
      isUnmounted = true
      if (reconnectTimeoutRef.current !== null) {
        clearTimeout(reconnectTimeoutRef.current)
      }
      if (wsRef.current) {
        wsRef.current.onopen = null
        wsRef.current.onmessage = null
        wsRef.current.onclose = null
        wsRef.current.onerror = null
        wsRef.current.close()
        wsRef.current = null
      }
    }
  }, [deviceId, localId])

  // filter by workstation
  const [selectedWs, setSelectedWs] = useState<number | 'all'>('all')
  const filtered = entries.filter((e) => {
    if (selectedWs === 'all') return true
    if (e.comanda.info.workstation_id === selectedWs) return true
    const t = terminalMap.get(e.comanda.terminal.terminal_id)
    return t?.workstation_id === selectedWs
  })

  // split in-progress vs pending
  const inProgress = filtered.filter((e) => e.status === 'ready')
  const pending = filtered.filter((e) => e.status === 'start')

  if (loading) return <LoadingSpinner />

  return (
    <div className="kds-container">
      <Header
        connectionStatus={connectionStatus}
        onZoomIn={zoomIn}
        onZoomOut={zoomOut}
        onRefresh={handleRefresh}
        onBack={() => navigate(-1)}
        selectedWs={selectedWs}
        setSelectedWs={setSelectedWs}
        workstations={workstations}
      />

      <div
        className="kds-scale-wrapper"
  style={{ transform: `scale(${scale})`, transformOrigin: 'top center' }}
      >
        <section className="kds-section">
          <h2>En Progreso</h2>
          <div className="kds-cards">
            {inProgress.map((e) => (
              <ComandaCard
                key={e.entryId}
                e={e}
                entries={entries}
                setEntries={setEntries}
                batchColorRef={batchColorRef}
              />
            ))}
          </div>
        </section>

        <section className="kds-section">
          <h2>Pendientes</h2>
          <div className="kds-cards">
            {pending.map((e) => (
              <ComandaCard
                key={e.entryId}
                e={e}
                entries={entries}
                setEntries={setEntries}
                batchColorRef={batchColorRef}
              />
            ))}
          </div>
        </section>
      </div>
    </div>
  )
}

export default KDS
